"use client";

import { NextIntlClientProvider } from "next-intl";
import { createRoot, type Root } from "react-dom/client";
import { WidgetInstanceScope } from "@/components/widget/widget-instance-scope";
import { WidgetShell } from "@/components/widget/widget-shell";
import { createEmbedLocaleController } from "@/lib/embed-locale-controller";
import { detectClientLocale } from "@/lib/locale-detect";
import { isSupportedLocale, type SupportedLocale } from "@/lib/locales";
import { subscribeRuntimeLocale, subscribeRuntimeLocaleReset } from "@/lib/runtime-locale";
import { BUILD_TRUST_TAG } from "@/lib/trusted-host-build";
import {
  type PreviewTabSignal,
  type VisitorIdentity,
  type VisitorPrefill,
  type WidgetEnv,
  WidgetEnvProvider,
} from "@/lib/use-widget-env";
import type { InjectableWidgetConfig, WidgetConfig } from "@/lib/widget-config";

// Compiled Tailwind layer + theme tokens, inlined as a string at
// build time. Vite resolves the `?inline` suffix; the matching
// type declaration lives in `styles.css.d.ts`. Adopted into the
// Shadow DOM so all utility classes resolve inside the custom
// element without leaking to (or absorbing) host page styles.
import { readBoolAttr } from "./read-bool-attr";
import { readOffsetAttrs } from "./read-offset";
import widgetStyles from "./styles.css?inline";

/**
 * Boot-time settings the host can stash on the global BEFORE the
 * `<wexio-widget>` element upgrades — the
 * `window.wexioSettings` convention. Today it carries an optional
 * pre-resolved `config` so a script-tag host that already has the
 * integration config can skip the `GET /api/web/config` bootstrap; a
 * full object can't ride on an HTML attribute, hence the global.
 */
interface WexioSettings {
  config?: InjectableWidgetConfig;
  /** Boot-time known-user identity (parity with the React `user` prop). The
   *  imperative `identify()` call overrides it. */
  user?: VisitorIdentity;
  /** Partial config patch merged OVER the resolved config (BE or injected) —
   *  the web-component / script-tag equivalent of the React `configOverride`
   *  prop. Lets a host brand the widget (theme / colours / greeting / features)
   *  without supplying a full `config`. Presentation only. */
  configOverride?: Partial<WidgetConfig>;
}

function readWexioSettings(): WexioSettings | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { wexioSettings?: WexioSettings }).wexioSettings;
}

const ATTR_PUBLIC_KEY = "public-key";
const ATTR_LOCALE = "locale";
// `mode="demo"` forces the bundled mock content even when the
// web-component bundle has a fallback PK inlined from
// `NEXT_PUBLIC_WEXIO_DEMO_PK`. Used by `widget-demo.html` so the
// public-facing demo is a true visitor experience, not a hidden
// production embed against the marketing integration. `production`
// and `preview` are accepted for symmetry; omit the attribute for
// the standard auto-resolve (`production` when a PK is present,
// `demo` otherwise).
const ATTR_MODE = "mode";
// Unverified default profile to pre-fill / auto-attach the prechat.
const ATTR_PREFILL_NAME = "prefill-name";
const ATTR_PREFILL_EMAIL = "prefill-email";
const ATTR_PREFILL_PHONE = "prefill-phone";
// Host-forced light/dark theme. Reactive — mutate it (e.g. the host's own
// dark-mode toggle flipped) and the widget flips live.
const ATTR_THEME = "theme";
// Host nudge (px) of the launcher + panel away from the anchored corner, to
// clear the host's own fixed furniture. Reactive.
const ATTR_OFFSET_X = "offset-x";
const ATTR_OFFSET_Y = "offset-y";
// Open state: `open` is controlled (reactive; "true"/"false", absent = uncontrolled),
// `default-open` only sets the state on mount. Navigation is the `previewTab` JS
// property (an object, so no attribute).
const ATTR_OPEN = "open";
const ATTR_DEFAULT_OPEN = "default-open";
// BE preview token (`createWebPreviewToken`), DASHBOARD ONLY. A token makes the widget run a real
// session hidden from the inbox, analytics and metering, with in-memory storage. Reactive: set a new
// value to refresh it. The widget asks for a new one with the `wexio:preview-token-request` event.
const ATTR_PREVIEW_TOKEN = "preview-token";

/**
 * `<wexio-widget public-key="pk_live_..." locale="en">` — microfrontend
 * delivery for the Wexio web messenger.
 *
 * Same React tree as the iframe loader, mounted inside a Shadow DOM on
 * the host page instead. Style isolation comes from the Shadow DOM's
 * adopted stylesheet; the `WidgetEnvProvider` swaps the postMessage
 * resize/close path for `CustomEvent`s the host can listen for.
 *
 * Host integration shape:
 *
 *   const el = document.querySelector("wexio-widget");
 *   el.addEventListener("wexio:resize", (e) => { ... e.detail.width / height });
 *   el.addEventListener("wexio:close",  ()  => { ... });
 *
 * The host can update the public-key at runtime by mutating the
 * attribute; the React tree re-resolves config without a remount.
 */
export class WexioWidgetElement extends HTMLElement {
  static get observedAttributes() {
    return [
      ATTR_PUBLIC_KEY,
      ATTR_LOCALE,
      ATTR_MODE,
      ATTR_PREFILL_NAME,
      ATTR_PREFILL_EMAIL,
      ATTR_PREFILL_PHONE,
      ATTR_THEME,
      ATTR_OFFSET_X,
      ATTR_OFFSET_Y,
      ATTR_OPEN,
      ATTR_DEFAULT_OPEN,
      ATTR_PREVIEW_TOKEN,
    ];
  }

  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;
  private styleSheet: CSSStyleSheet | null = null;
  // Resolved UI locale. `null` until the first render computes a
  // provisional value (explicit `locale=` attr, else browser). Phase 2:
  // `onLocaleStrategy` (below) re-resolves once the config's strategy
  // is known and re-renders if the language changed.
  private currentLocale: SupportedLocale | null = null;
  // Messages for the resolved locale: renders bundled English first and swaps when the locale chunk
  // lands (`lib/embed-locale-controller`); `renderTree` bails out when the element has no root.
  private readonly localeController = createEmbedLocaleController(() => this.renderTree());
  // Known-user identity set by the host via `identify(user)`. `null`
  // keeps the visitor anonymous.
  private identity: VisitorIdentity | null = null;
  // Runtime-locale subscription teardown — wired in `connectedCallback`,
  // removed in `disconnectedCallback`. Lets the Profile-tab language
  // picker flip the rendered locale live without a host-side remount.
  private unsubscribeRuntimeLocale: (() => void) | null = null;
  private unsubscribeRuntimeLocaleReset: (() => void) | null = null;
  // The language the visitor picked in the Profile tab this session. Once set it is pinned: the
  // operator-strategy re-resolve below runs after EVERY render (each render builds a fresh env, so
  // `useWidgetConfig` re-reports the strategy) and would snap the UI back to the browser language
  // before the saved-language cookie exists.
  private runtimePick: SupportedLocale | null = null;
  // Navigation request set by the host (`el.previewTab = { tab, nonce }`).
  private previewTabValue: PreviewTabSignal | undefined;
  get previewTab(): PreviewTabSignal | undefined {
    return this.previewTabValue;
  }
  set previewTab(next: PreviewTabSignal | undefined) {
    this.previewTabValue = next;
    if (this.root) this.renderTree();
  }

  connectedCallback() {
    if (this.root) return; // double-attach guard (React strict-mode in dev)

    // A host may set `previewTab` before the element upgrades: that lands as an own data property
    // shadowing the accessor above. Move it onto the accessor.
    if (Object.hasOwn(this, "previewTab")) {
      const early = (this as { previewTab?: PreviewTabSignal }).previewTab;
      delete (this as { previewTab?: PreviewTabSignal }).previewTab;
      this.previewTabValue = early;
    }

    const shadow = this.shadowRoot ?? this.attachShadow({ mode: "open" });

    // Browsers without constructable stylesheets (older Safari /
    // Firefox-ESR) fall back to a `<style>` node — same isolation
    // story, slightly heavier on style recomputation.
    if (typeof CSSStyleSheet !== "undefined" && "replaceSync" in CSSStyleSheet.prototype) {
      this.styleSheet = new CSSStyleSheet();
      this.styleSheet.replaceSync(widgetStyles);
      shadow.adoptedStyleSheets = [...(shadow.adoptedStyleSheets ?? []), this.styleSheet];
    } else {
      const styleNode = document.createElement("style");
      styleNode.textContent = widgetStyles;
      shadow.appendChild(styleNode);
    }

    this.mountPoint = document.createElement("div");
    // Build flavour tag (see `BUILD_TRUST_TAG`): the public/private boundary check greps for it.
    this.mountPoint.setAttribute("data-wexio-build", BUILD_TRUST_TAG);
    // Apply the theme-data attribute to the shadow root host so the
    // existing `[data-theme="dark"]` CSS selectors continue to match
    // when `useThemeMode` mutates it. The host attribute is what the
    // selector looks for; we just need a stable target.
    this.mountPoint.style.display = "contents";
    shadow.appendChild(this.mountPoint);

    this.root = createRoot(this.mountPoint);
    this.renderTree();

    // Subscribe to the runtime-locale bus — `useVisitorProfilePreferences`
    // fires when the visitor saves a new language in the Profile tab.
    // Explicit `locale=` attribute pins the host's choice (no live flip).
    this.unsubscribeRuntimeLocale = subscribeRuntimeLocale((next) => {
      if (this.getAttribute(ATTR_LOCALE)) return;
      if (!isSupportedLocale(next)) return;
      this.runtimePick = next;
      if (next === this.currentLocale) return;
      this.currentLocale = next;
      this.renderTree();
    });
    // A rejected language pick was taken back and none is saved: drop the pin and re-resolve.
    this.unsubscribeRuntimeLocaleReset = subscribeRuntimeLocaleReset(() => {
      this.runtimePick = null;
      this.currentLocale = null;
      this.renderTree();
    });
  }

  attributeChangedCallback(name: string, _old: string | null, _next: string | null) {
    // A public-key change re-renders below: `WidgetInstanceScope` then builds a fresh instance (key,
    // token, Apollo cache) and remounts the widget, so nothing of the old integration's session survives.
    // A runtime `locale=` change must re-resolve from scratch — drop the
    // cached value so `renderTree` recomputes the provisional locale
    // (and phase 2 re-applies the strategy on the next config report).
    if (name === ATTR_LOCALE) {
      this.currentLocale = null;
    }
    if (this.root) this.renderTree();
  }

  disconnectedCallback() {
    this.unsubscribeRuntimeLocale?.();
    this.unsubscribeRuntimeLocale = null;
    this.unsubscribeRuntimeLocaleReset?.();
    this.unsubscribeRuntimeLocaleReset = null;
    this.root?.unmount();
    this.root = null;
    this.mountPoint = null;
    this.styleSheet = null;
  }

  /**
   * Log a known user into the widget — a known-user login / boot:
   *
   *   const el = document.querySelector("wexio-widget");
   *   el.identify({ userId, userHash, name, email, attributes });
   *
   * `userId` + `userHash` are the verified identity (`userHash` is an
   * HMAC-SHA256 of `userId` computed server-side with the integration
   * secret); the rest is profile data. Pass `null` to clear back to
   * anonymous. Safe to call before the element connects — the value is
   * stored and applied on mount.
   */
  identify(user: VisitorIdentity | null) {
    this.identity = user;
    if (this.root) this.renderTree();
  }

  /** Unverified default profile from the `prefill-*` attributes (static
   *  host markup). `undefined` when none are set. */
  private readPrefill(): VisitorPrefill | undefined {
    const name = this.getAttribute(ATTR_PREFILL_NAME) ?? undefined;
    const email = this.getAttribute(ATTR_PREFILL_EMAIL) ?? undefined;
    const phone = this.getAttribute(ATTR_PREFILL_PHONE) ?? undefined;
    if (!name && !email && !phone) return undefined;
    return { name, email, phone };
  }

  /** Host-forced theme from the `theme` attribute, validated.
   *  `undefined` when unset / invalid ⇒ no override. */
  private readTheme(): "light" | "dark" | "auto" | undefined {
    const t = this.getAttribute(ATTR_THEME);
    return t === "light" || t === "dark" || t === "auto" ? t : undefined;
  }

  /** Launcher offset (px) from the `offset-x` / `offset-y` attributes, per axis (see `readOffsetAttrs`). */
  private readOffset(): { x?: number; y?: number } | undefined {
    return readOffsetAttrs(this.getAttribute(ATTR_OFFSET_X), this.getAttribute(ATTR_OFFSET_Y));
  }

  private buildEnv(): WidgetEnv {
    // `mode="demo"` is the demo escape hatch — forces the bundled mock
    // content regardless of any PK that might otherwise auto-resolve
    // (the Vite-inlined `NEXT_PUBLIC_WEXIO_DEMO_PK` fallback, the
    // `?pk=` URL param, even a `public-key` attribute the host left
    // on for testing). The demo HTML pages on `cdn.wexio.io` use this
    // so they always show the visitor-facing demo, never a hidden
    // production embed against the marketing PK.
    const modeAttr = this.getAttribute(ATTR_MODE);
    const forcedDemo = modeAttr === "demo";
    const explicitMode: "production" | "preview" | "demo" | undefined =
      modeAttr === "production" || modeAttr === "preview" || modeAttr === "demo"
        ? modeAttr
        : undefined;
    // Skip the public-key attribute when explicitly in demo mode so the
    // mode-resolver doesn't auto-promote to "production". Otherwise
    // honour the attribute as before.
    const publicKey = forcedDemo ? undefined : (this.getAttribute(ATTR_PUBLIC_KEY) ?? undefined);
    return {
      kind: "web-component",
      publicKeyOverride: publicKey,
      modeOverride: explicitMode ?? (publicKey ? "production" : "demo"),
      // Shadow DOM mount point — anything portalled here (e.g. the
      // media lightbox) stays inside the widget's Tailwind stylesheet
      // scope. Without this, `createPortal(..., env.themeRoot ?? document.body)`
      // falls through to `document.body` and the portalled element
      // renders unstyled (`bg-black/85`, `fixed`, `z-2147483647` all
      // become no-ops because the Shadow-DOM-scoped CSS doesn't reach
      // light-DOM nodes).
      themeRoot: this.mountPoint ?? undefined,
      // The widget lives in this element's Shadow DOM, so the operator's
      // theme-token stylesheet (primary / colours from config + host
      // overrides) must be adopted INTO the shadow root — a `<style>` in the
      // document `<head>` can't reach it. Same wiring as the React embed.
      styleRoot: this.shadowRoot ?? undefined,
      // Host-forced light/dark theme (`theme` attribute). Reactive via
      // `attributeChangedCallback` → `renderTree()` → fresh `buildEnv()`.
      themeOverride: this.readTheme(),
      // Host launcher offset (px) from `offset-x` / `offset-y`. Reactive via
      // attributeChangedCallback -> renderTree -> fresh buildEnv.
      offset: this.readOffset(),
      // Open state + navigation (attributes `open` / `default-open`, property `previewTab`).
      open: readBoolAttr(this.getAttribute(ATTR_OPEN)),
      defaultOpen: readBoolAttr(this.getAttribute(ATTR_DEFAULT_OPEN)),
      previewTab: this.previewTabValue,
      // Full config injection (`window.wexioSettings.config`) — a script-
      // tag host that already has the integration config can set it on
      // the global before the element upgrades, and the widget boots
      // without the `GET /api/web/config` round-trip. An object can't
      // ride on an attribute, so the global is the injection point here.
      configBase: readWexioSettings()?.config,
      // Partial host branding patch (`window.wexioSettings.configOverride`) —
      // merged over the resolved config by `useWidgetConfig`, same as the React
      // `configOverride` prop. Read at build time (set before upgrade).
      configOverride: readWexioSettings()?.configOverride,
      // Known-user identity: the imperative `identify()` call wins; otherwise
      // the boot-time `window.wexioSettings.user` (parity with the React `user`
      // prop + the iframe loader's `wexioSettings.user`).
      visitorIdentity: this.identity ?? readWexioSettings()?.user,
      visitorPrefill: this.readPrefill(),
      onResize: (width, height) => {
        this.dispatchEvent(
          new CustomEvent("wexio:resize", {
            detail: { width, height },
            bubbles: true,
            composed: true,
          }),
        );
      },
      // Fires when the visitor opens the panel (launcher tap, peek tap, …) —
      // parity with the React `onOpen` prop. Pairs with `wexio:close`.
      onOpen: () => {
        this.dispatchEvent(new CustomEvent("wexio:open", { bubbles: true, composed: true }));
      },
      onClose: () => {
        this.dispatchEvent(new CustomEvent("wexio:close", { bubbles: true, composed: true }));
      },
      // Quick-reply click on a welcome message.
      onMessageAction: (detail) => {
        this.dispatchEvent(
          new CustomEvent("wexio:message-action", { detail, bubbles: true, composed: true }),
        );
      },
      // The BE rejected the preview token: the host (the dashboard) sets a new `preview-token`.
      onPreviewTokenRequest: (reason) => {
        this.dispatchEvent(
          new CustomEvent("wexio:preview-token-request", {
            detail: { reason },
            bubbles: true,
            composed: true,
          }),
        );
      },
      // Phase 2 of locale resolution — `useWidgetConfig` reports the
      // operator's strategy here. Re-resolve and re-render if the
      // language changed; an explicit `locale=` attr pins it (no-op).
      // The web-component embed is always visitor-facing (script-tag
      // host page), so the `?lang=` / cookie escape hatches stay active.
      onLocaleStrategy: (strategy: string) => {
        const pinned = this.getAttribute(ATTR_LOCALE);
        if (isSupportedLocale(pinned)) return;
        if (this.runtimePick) return;
        const next = detectClientLocale(strategy);
        if (next !== this.currentLocale) {
          this.currentLocale = next;
          this.renderTree();
        }
      },
    };
  }

  private renderTree() {
    if (!this.root) return;
    // Provisional locale on first render; phase 2 may override below.
    if (this.currentLocale === null) {
      this.currentLocale = pickLocale(this.getAttribute(ATTR_LOCALE));
    }
    const locale = this.currentLocale;
    // Render at once (bundled English on the first paint, the previous language on a switch) and
    // re-render when the locale chunk lands: a stalled request never leaves the page without a launcher.
    const shown = this.localeController.request(locale);
    const env = this.buildEnv();
    this.root.render(
      <WidgetInstanceScope
        publicKey={env.publicKeyOverride}
        previewToken={this.getAttribute(ATTR_PREVIEW_TOKEN) || undefined}
      >
        <NextIntlClientProvider
          locale={shown.locale}
          messages={shown.messages}
          // Browser zone; UTC fallback for SSR / non-`Intl` runtimes.
          // Silences next-intl's ENVIRONMENT_FALLBACK warning.
          timeZone={
            typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC"
          }
        >
          <WidgetEnvProvider env={env}>
            <WidgetShell />
          </WidgetEnvProvider>
        </NextIntlClientProvider>
      </WidgetInstanceScope>,
    );
  }
}

function pickLocale(raw: string | null): SupportedLocale {
  // Explicit `locale="…"` attribute wins (operator pinned a language).
  if (isSupportedLocale(raw)) return raw;
  // Otherwise fall back to the visitor's browser: `?lang=` / cookie /
  // `navigator.language`, best-matched against the supported set.
  return detectClientLocale();
}
